import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Option,
  Typography,
  Autocomplete,
  AutocompleteProps,
} from '@beeline/design-system-react/extracted-styles';

import { Town } from 'src/api/types';
import { getTownsState } from 'src/store/towns/selectors';
import { fetchTowns } from 'src/store/towns/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { truphy } from 'src/utils/truphy';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

export type TownSelectItem = Option<Town>;

export interface TownSelectProps
  extends Omit<
    AutocompleteProps<Town>,
    | 'label'
    | 'size'
    | 'fullWidth'
    | 'placeholder'
    | 'noOptionsText'
    | 'options'
    | 'loading'
    | 'renderValue'
    | 'multiple'
    | 'values'
    | 'onChange'
    | 'value'
    | 'type'
    | 'onInputChange'
    | 'onInputClear'
  > {
  town: TownSelectItem | undefined;
  onChange: (value: TownSelectItem | undefined) => void;
  filterOptions?: (option: TownSelectItem) => boolean;
}

const renderValue = (town: TownSelectItem | null): string =>
  town?.value.townName ?? '';

const defaultMakeOption = (option: TownSelectItem | null): JSX.Element => {
  return option ? (
    <TooltipWrapper title={option.value.townName}>
      <Typography variant='body2' ellipsis>
        {option.value.townName}
      </Typography>
    </TooltipWrapper>
  ) : (
    <></>
  );
};

export const toTownSelectItem = (
  town: Town | undefined,
): TownSelectItem | undefined =>
  town && {
    id: town.townId,
    value: town,
  };

export const toTown = (item: TownSelectItem | undefined): Town | undefined =>
  item?.value;

export const TownSelect: FC<TownSelectProps> = ({
  town,
  onChange,
  makeOption = defaultMakeOption,
  filterOptions = truphy,
  ...rest
}) => {
  const [input, setInput] = useState('');

  const dispatch = useAppDispatch();
  const { towns, loading } = useSelector(getTownsState);

  const options = useMemo((): TownSelectItem[] => {
    const result: TownSelectItem[] = [];
    for (const cachedTown of towns.values()) {
      const item = toTownSelectItem(cachedTown);
      if (item && filterOptions(item)) result.push(item);
    }
    return result;
  }, [filterOptions, towns]);

  const filteredOptions = useMemo((): TownSelectItem[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.townName.toLowerCase().includes(search),
    );
  }, [input, options]);

  useEffect(() => {
    dispatch(fetchTowns(true));
  }, [dispatch]);

  const handleChange = useCallback(
    (nextValue: TownSelectItem | undefined) => {
      onChange(nextValue);
    },
    [onChange],
  );

  const handleClear = useCallback(async () => {
    setInput('');
    handleChange(undefined);
  }, [handleChange]);

  return (
    <Autocomplete
      {...(rest as AutocompleteProps<TownSelectItem>)}
      size='medium'
      label='Город'
      fullWidth
      placeholder='выберите город'
      noOptionsText='нет городов'
      options={filteredOptions}
      loading={loading}
      renderValue={renderValue}
      makeOption={makeOption}
      multiple={false}
      value={town ?? null}
      onChange={handleChange}
      onInputChange={setInput}
      onInputClear={handleClear}
    />
  );
};
